import { useEffect, type ReactNode } from "react"

import { Button, Loader } from "@coval/ui"
import { Check, Sparkles } from "lucide-react"

import { cn } from "@/utils/cn"

import type { PersonaCustomVoiceOption } from "../types"
import { AudioPreviewButton } from "./AudioPreviewButton"
import { getLanguageDisplayName } from "./personaVoiceLibraryUtils"

interface CustomVoicePreviewProps {
  voice: PersonaCustomVoiceOption
  previewUrl?: string
  previewFailed?: boolean
  getPreviewUrl?: (voiceId: string) => string | undefined
  currentlyPlayingAudioId?: string | null
  onPlayingChange?: (playingId: string | null) => void
}

export function CustomVoiceOption({
  voice,
  previewUrl,
  previewFailed,
  getPreviewUrl,
  isSelected,
  isDisabled,
  currentlyPlayingAudioId,
  onPlayingChange,
  onSelect,
  action,
}: CustomVoicePreviewProps & {
  isSelected: boolean
  isDisabled: boolean
  onSelect: () => void
  /** Controls shown left of the voice sample, e.g. Clear and Change Voice. */
  action?: ReactNode
}) {
  return (
    <div
      role="group"
      aria-label={`${voice.name} custom voice option`}
      className={cn(
        // The Voice card is already the container, so the chosen voice sits on
        // the section's own surface behind an outline rather than a fill.
        "flex min-h-28 items-stretch gap-2 rounded-md border border-coval-border p-3 transition-colors",
        "hover:bg-surface-default-hover",
        isDisabled && "opacity-60",
      )}
    >
      <button
        type="button"
        aria-pressed={isSelected}
        disabled={isDisabled}
        onClick={onSelect}
        className="flex min-w-0 flex-1 flex-col text-left"
      >
        <div className="flex items-center gap-1.5">
          <Sparkles className="size-4 shrink-0 text-coval-contrast" />
          <span className="truncate text-sm font-semibold">{voice.name}</span>
          {isSelected && (
            <Check className="size-4 shrink-0 text-coval-contrast" />
          )}
        </div>
        <span className="mt-1 line-clamp-2 text-xs leading-5 text-coval-text-muted">
          {voice.description}
        </span>
        <span className="mt-auto pt-2 text-xs text-coval-text-muted">
          {getLanguageDisplayName(voice.language)} · Custom
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-2">
        {action}
        <CustomVoicePreviewButton
          voice={voice}
          previewUrl={previewUrl}
          previewFailed={previewFailed}
          getPreviewUrl={getPreviewUrl}
          currentlyPlayingAudioId={currentlyPlayingAudioId}
          onPlayingChange={onPlayingChange}
        />
      </div>
    </div>
  )
}

export function CustomVoicePreviewButton({
  voice,
  previewUrl,
  previewFailed = false,
  getPreviewUrl,
  currentlyPlayingAudioId,
  onPlayingChange,
}: CustomVoicePreviewProps) {
  useEffect(() => {
    if (!previewUrl && !previewFailed) {
      getPreviewUrl?.(voice.id)
    }
  }, [getPreviewUrl, previewFailed, previewUrl, voice.id])

  if (previewFailed) {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => getPreviewUrl?.(voice.id)}
      >
        Retry preview
      </Button>
    )
  }

  if (!previewUrl) {
    return (
      <span
        className="inline-flex size-8 shrink-0 items-center justify-center"
        aria-label={`Loading ${voice.name} preview`}
      >
        <Loader size="sm" label={`Loading ${voice.name} preview`} />
      </span>
    )
  }

  return (
    <AudioPreviewButton
      previewUrl={previewUrl}
      audioLabel={voice.name}
      currentlyPlayingId={currentlyPlayingAudioId}
      onPlayingChange={onPlayingChange}
      className="shrink-0 self-start"
    />
  )
}
